<template>
  <div class="review-list-page">
    <!-- 页面头部 -->
    <div class="review-list-header">
      <div class="review-list-header-text">
        <!-- <h1 class="review-list-header-title">
          <a-button class="wb-back" @click="goBack">
            <ArrowLeftOutlined /> </a-button
          >全部最近审查任务
        </h1> -->
        <div class="review-list-header-back">
          <img
            src="@/assets/icons/i-return.png"
            style="width: 36px; height: 36px; cursor: pointer"
            @click="goBack"
          />
          <div class="review-list-header-title">全部最近审查任务</div>
        </div>

        <div class="review-list-header-desc">
          查看全部合同审查记录，并继续编辑草稿、审查中或已完成的任务。
        </div>
      </div>
      <!-- <button class="back-btn" @click="goBack">
        <ArrowLeftOutlined />
        <span>返回合同审查</span>
      </button> -->
    </div>

    <!-- 搜索栏 -->
    <div class="review-list-toolbar">
      <div class="review-list-search">
        <SearchOutlined class="search-icon" />
        <input
          v-model="keyword"
          type="text"
          placeholder="搜索合同标题"
          class="search-input"
        />
      </div>
    </div>

    <!-- 列表 -->
    <template v-if="list.length">
      <div ref="listScrollRef" class="review-history-list">
        <article
          v-for="item in list"
          :key="item.id"
          class="review-history-card"
          @click="handleEdit(item)"
        >
          <div class="review-history-icon">
            <SafetyOutlined />
          </div>
          <div class="review-history-main">
            <div class="review-history-title" :title="item.fileName">
              <span class="review-history-title-text">{{ item.fileName }}</span>
              <span v-if="item.isDraft" class="review-history-draft-tag"
                >草稿合同</span
              >
            </div>
            <div class="review-history-meta">{{ buildMeta(item) }}</div>
          </div>
          <span class="history-status" :class="`status-${item.statusClass}`">
            {{ item.statusLabel }}
          </span>
          <div class="review-history-time">{{ formatTime(item.time) }}</div>
          <div class="card-actions">
            <button class="edit-btn" @click.stop="handleEdit(item)">
              编辑
            </button>
            <button class="remove-btn" @click.stop="onDelete(item)">
              删除
            </button>
          </div>
        </article>
      </div>

      <!-- 分页器 -->
      <div class="review-history-pagination">
        <a-pagination
          v-model:current="currentPage"
          v-model:pageSize="pageSize"
          :total="total"
          :show-total="(total) => `共 ${total} 条`"
          show-size-changer
          :page-size-options="['10', '20', '50']"
          @change="onPageChange"
        />
      </div>
    </template>

    <!-- 空状态 -->
    <div v-else class="review-history-empty">
      <a-empty />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { message, Modal } from "ant-design-vue";
import { SearchOutlined, SafetyOutlined } from "@ant-design/icons-vue";
import { getHistoryPage, deleteReviewHistory } from "@/api/contractReview";

const router = useRouter();

const loading = ref(false);
const keyword = ref("");
const list = ref([]);
const total = ref(0);

// 分页
const currentPage = ref(1);
const pageSize = ref(10);

// 列表滚动容器（翻页时回到顶部）
const listScrollRef = ref(null);

// 后端字段命名尚未最终确定，对常见命名做兜底
const STATUS_MAP = {
  Completed: { label: "审查完成", class: "done" },
  Processing: { label: "审查中", class: "running" },
  InProgress: { label: "审查中", class: "running" },
  Draft: { label: "草稿", class: "draft" },
};

const PARTY_LABEL = {
  PartyA: "甲方",
  PartyB: "乙方",
  Lessee: "承租方",
};

const SCALE_LABEL = {
  Strong: "强势",
  Balanced: "均势",
  Weak: "弱势",
};

const normalizeItem = (raw) => {
  const status = raw.status ?? raw.reviewStatus ?? raw.Status;
  const statusInfo = STATUS_MAP[status] || {
    label: status === "Completed" ? "审查完成" : "待审查",
    class: status === "Completed" ? "done" : "draft",
  };
  const party = raw.stance ?? raw.party ?? raw.reviewParty ?? raw.Party;
  const scale = raw.scale ?? raw.reviewScale ?? raw.strategy ?? raw.Scale;
  return {
    id: raw.id ?? raw.contractId ?? raw.reviewId ?? raw.recordId,
    fileId: raw.fileId ?? raw.file_id ?? raw.uploadFileId,
    revisedFileId: raw.revisedFileId ?? raw.revised_file_id,
    fileName: raw.fileName ?? "未命名合同",
    // 草稿合同标记：true 显示"草稿合同"标签（后端可能返回字符串布尔，双兜底）
    isDraft: raw.isDraft === true || raw.isDraft === "true",
    time: raw.time ?? raw.reviewTime ?? raw.createTime ?? raw.createdAt,
    count: raw.count ?? raw.opinionCount ?? raw.opinionsCount ?? 0,
    status,
    partyLabel: party ? PARTY_LABEL[party] || party : null,
    scaleLabel: scale ? SCALE_LABEL[scale] || scale : null,
    statusLabel: statusInfo.label,
    statusClass: statusInfo.class,
  };
};

const fetchList = async () => {
  loading.value = true;
  try {
    const res = await getHistoryPage({
      page: currentPage.value, // 后端页码 0-based
      pageSize: pageSize.value,
      keyword: keyword.value.trim() || undefined,
    });
    // 兼容多种返回结构
    const data = res?.data ?? res ?? {};
    const rows =
      data.list ??
      data.items ??
      data.records ??
      (Array.isArray(data) ? data : []);
    list.value = rows.map(normalizeItem);
    console.log(
      "[ReviewList] 原始列表数据第一条，确认 revisedFileId 字段名:",
      rows[0],
    );
    total.value = data.total ?? data.totalCount ?? data.count ?? rows.length;
  } catch (err) {
    console.error("[ReviewList] getHistoryPage error:", err);
    list.value = [];
    total.value = 0;
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchList();
});

// 搜索：防抖后回到第一页并重新请求
let searchTimer = null;
watch(keyword, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    currentPage.value = 1;
    fetchList();
  }, 300);
});

const onPageChange = () => {
  fetchList();
  listScrollRef.value?.scrollTo({ top: 0, behavior: "smooth" });
};

const buildMeta = (item) => {
  const parts = [];
  if (item.partyLabel) parts.push(`审查立场：${item.partyLabel}`);
  if (item.scaleLabel) parts.push(`审查尺度：${item.scaleLabel}`);
  if (item.statusClass === "running") {
    parts.push("正在生成审查意见");
  } else if (item.statusClass === "draft") {
    parts.push("尚未开始审查");
  } else if (item.count) {
    parts.push(`${item.count} 条审查意见`);
  }
  return parts.join(" · ");
};

const formatTime = (time) => {
  if (!time) return "";
  const date = new Date(time);
  if (isNaN(date.getTime())) return String(time);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const handleEdit = (item) => {
  if (!item?.id) return;
  const query = { id: item.id, fileId: item.revisedFileId || item.fileId };
  if (item.statusClass === "draft") {
    router.push({ path: "/home/contract-review/requirements", query });
  } else {
    router.push({
      path: "/home/contract-review/result",
      query: { ...query, status: item.status },
    });
  }
};

/* 删除审查记录：确认框 → 调接口 → 刷新列表；
 * 当前页只剩这一条且不是第一页时回退一页，避免落在空页 */
const deletingId = ref(null);
const onDelete = (item) => {
  if (!item?.id || deletingId.value != null) return;
  Modal.confirm({
    title: "删除审查记录",
    content: `确定删除「${item.fileName || "未命名合同"}」吗？删除后不可恢复。`,
    okText: "确认",
    cancelText: "取消",
    onOk: async () => {
      deletingId.value = item.id;
      try {
        await deleteReviewHistory(item.id);
        message.success("已删除");
        if (list.value.length === 1 && currentPage.value > 1) {
          currentPage.value -= 1;
        }
        await fetchList();
      } catch (err) {
        console.error("[ReviewList] deleteReviewHistory error:", err);
        message.error("删除失败");
      } finally {
        deletingId.value = null;
      }
    },
  });
};

const goBack = () => {
  router.push("/home/contract-review");
};
</script>

<style>
/* 审查列表页显示时，禁止外层 .content-wrapper 自身滚动；
   页面用绝对定位占满 .content（已 position:relative），仅列表内部滚动 */
.content-wrapper:has(.review-list-page) {
  overflow: hidden;
}
</style>

<style lang="less" scoped>
.review-list-page {
  /* 用绝对定位占满 .content（已 position:relative），
     不依赖父级 flex 高度链，整页不滚动，仅列表区域内部滚动 */
  position: absolute;
  inset: 0;
  box-sizing: border-box;
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  line-height: 1.5;
}

/* 头部 */
.review-list-header {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}
.review-list-header-back {
  display: flex;
  align-items: center;
  gap: 12px;
}
.review-list-header-title {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.5px;
  color: #17243a;
  .wb-back {
    width: 36px;
    height: 36px;
    margin-right: 12px;
    align-items: center;
    justify-content: center;
    padding: 0;
    font-size: 16px;
    flex-shrink: 0;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
  }
}
.review-list-header-desc {
  margin-top: 10px;
  color: #65758e;
  font-size: 15px;
  line-height: 1.7;
}
.back-btn {
  flex-shrink: 0;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #dce4f0;
  border-radius: 10px;
  background: #fff;
  color: #31435f;
  font-size: 14px;
  font-weight: 650;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 0.18s, border-color 0.18s;
  .anticon {
    font-size: 16px;
  }
  &:hover {
    background: #f8fbff;
    border-color: #c9d6e8;
  }
}

/* 搜索栏 */
.review-list-toolbar {
  flex-shrink: 0;
  margin-top: 20px;
  min-height: 72px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fff;
  border: 1px solid #e5ebf3;
  border-radius: 16px;
  box-shadow: 0 8px 26px rgba(31, 53, 88, 0.06);
}
.review-list-search {
  position: relative;
  width: min(360px, 100%);
  flex: none;
  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #7d8ba1;
    font-size: 18px;
    pointer-events: none;
  }
  .search-input {
    width: 100%;
    height: 40px;
    padding: 0 13px 0 38px;
    border: 1px solid #d9e3f0;
    border-radius: 10px;
    background: #fff;
    color: #283b57;
    outline: none;
    font-size: 14px;
    transition: border-color 0.2s, box-shadow 0.2s;
    &::placeholder {
      color: #9aa6b7;
    }
    &:focus {
      border-color: #8fb6fb;
      box-shadow: 0 0 0 3px rgba(23, 104, 255, 0.07);
    }
  }
}

/* 列表（内部滚动） */
.review-history-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: grid;
  gap: 12px;
  margin-top: 16px;
  /* 让 grid 子项不被挤出，配合 align-content 起始对齐 */
  align-content: start;
  padding-right: 4px;
}
.review-history-list::-webkit-scrollbar {
  width: 6px;
}
.review-history-list::-webkit-scrollbar-thumb {
  background: rgba(23, 104, 255, 0.18);
  border-radius: 3px;
}
.review-history-list::-webkit-scrollbar-thumb:hover {
  background: rgba(23, 104, 255, 0.32);
}
.review-history-list::-webkit-scrollbar-track {
  background: transparent;
}
.review-history-card {
  min-height: 84px;
  padding: 14px 16px;
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr) 112px 156px 144px;
  align-items: center;
  column-gap: 14px;
  border: 1px solid #e1e8f2;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 4px 14px rgba(31, 57, 105, 0.025);
  transition: border-color 0.18s, box-shadow 0.18s, transform 0.18s;
  cursor: pointer;
  &:hover {
    border-color: #c8d8ef;
    box-shadow: 0 8px 22px rgba(31, 57, 105, 0.06);
    transform: translateY(-1px);
  }
}
.review-history-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #edf4ff;
  color: #1768ff;
  display: grid;
  place-items: center;
  .anticon {
    font-size: 20px;
  }
}
.review-history-main {
  min-width: 0;
}
.review-history-title {
  font-size: 15px;
  font-weight: 780;
  color: #243752;
  /* flex 布局：文件名超长时省略号截断，草稿标签不被挤压截断 */
  display: flex;
  align-items: center;
  gap: 8px;
  .review-history-title-text {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .review-history-draft-tag {
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 400;
    line-height: 1;
    padding: 4px 8px;
    border-radius: 8px;
    color: #64748f;
    background: #eef1f6;
  }
}
.review-history-meta {
  margin-top: 7px;
  color: #718097;
  font-size: 12px;
  line-height: 1.55;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.history-status {
  justify-self: start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: max-content;
  height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  &.status-done {
    background: #eaf8f2;
    color: #16825e;
  }
  &.status-running {
    background: #edf4ff;
    color: #1768ff;
  }
  &.status-draft {
    background: #f2f4f7;
    color: #69778d;
  }
}
.review-history-time {
  color: #6f7e94;
  font-size: 12px;
  white-space: nowrap;
}
/* 操作按钮组（编辑 / 删除） */
.card-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
.edit-btn,
.remove-btn {
  height: 32px;
  min-width: 60px;
  padding: 0 12px;
  border: 1px solid #dce4f0;
  border-radius: 8px;
  background: #fff;
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
  transition: background 0.18s, border-color 0.18s;
}
.edit-btn {
  color: #31435f;
  &:hover {
    background: #f8fbff;
    border-color: #c9d6e8;
  }
}
/* 类名不用 delete-btn：全局 enterprise-surface.css 有
   .content-wrapper .delete-btn { color: ... !important }（AI 聊天界面的覆盖），
   会把本页按钮文字压成灰色 */
.remove-btn {
  color: #e34d59;
  border-color: #ffccc7; /* antd red-2 */
  &:hover {
    background: #fff1f0; /* antd red-1 */
    border-color: #ffa39e; /* antd red-3 */
  }
}

/* 空状态（撑满剩余空间） */
.review-history-empty {
  flex: 1;
  min-height: 0;
  margin-top: 16px;
  display: grid;
  place-items: center;
  text-align: center;
  color: #7c899d;
  background: #fff;
  border: 1px solid #e5ebf3;
  border-radius: 16px;
  box-shadow: 0 8px 26px rgba(31, 53, 88, 0.06);
}

/* 分页器（固定在页面底部） */
.review-history-pagination {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 20px;
  padding-bottom: 4px;
}

@media (max-width: 1320px) {
  .review-history-card {
    grid-template-columns: 42px minmax(0, 1fr) 104px 142px 136px;
    column-gap: 10px;
  }
}
</style>
